import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { Settings, LogOut } from "lucide-react";
import { RootState } from "../../redux/store";
import {
  fetchCities,
  setCitySearchQuery,
  setSelectedCity,
} from "../../redux/slices/citySlice";
import { City } from "../../types/city.types";
import { logoutCall } from "../../services/authService";
import { logout } from "../../redux/slices/authSlice";

interface AdminLayoutProps {
  children: React.ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const cityDropdownRef = useRef<HTMLDivElement>(null);
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const dispatch = useDispatch();

  const {
    availableCities,
    selectedCity,
    citySearchQuery,
    latitude,
    longitude,
  } = useSelector((state: RootState) => state.city);
  const user = useSelector((state: RootState) => state.auth.user);

  const menu = [
    { name: "Dashboard", path: "/admin" },
    { name: "Bookings", path: "/admin/bookings" },
    { name: "Orders", path: "/admin/orders" },
    { name: "Products", path: "/admin/products" },
    { name: "Washes", path: "/admin/washes" },
    { name: "Users", path: "/admin/users" },
    { name: "Locations", path: "/admin/locations" },
    { name: "Cars", path: "/admin/cars" },
  ];

  const isActive = (path: string) => {
    if (path === "/admin") {
      return location.pathname === "/admin";
    }
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    if (user?.role !== "admin") {
      window.location.href = "/";
    }
  });

  useEffect(() => {
    dispatch(setCitySearchQuery("All Cities"));
    dispatch(
      setSelectedCity({
        _id: "all",
        city: "All cities",
        country: "",
      })
    );

    const handleClick = (e: MouseEvent) => {
      if (
        cityDropdownRef.current &&
        !cityDropdownRef.current.contains(e.target as Node)
      ) {
        setIsCityDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [dispatch]);

  useEffect(() => {
    if (citySearchQuery === "All Cities") return;

    const delay = setTimeout(() => {
      dispatch(
        fetchCities({
          search: citySearchQuery,
          lat: latitude || 0,
          lng: longitude || 0,
          selectedCity,
        }) as any
      );
    }, 300);

    return () => clearTimeout(delay);
  }, [citySearchQuery]);

  const handleLogout = () => {
    dispatch(logout());
    logoutCall();
    window.location.href = "/";
  };

  return (
    <div className="flex h-screen bg-primaryBg dark:bg-primaryBgDark dark:text-primaryTextDark text-primaryText custom-scroll">
      <aside className="w-64 bg-popupLight dark:bg-popupDark shadow-md p-6 flex flex-col">
        <div className="flex-1">
          <h2 className="text-xl font-bold mb-6 cursor-pointer">
            <Link to="/admin">{t("appName")}</Link>
          </h2>

          <div
            className="relative mb-4 rounded-lg shadow"
            ref={cityDropdownRef}
          >
            <input
              type="text"
              value={citySearchQuery}
              onFocus={() => setIsCityDropdownOpen(true)}
              onChange={(e) => dispatch(setCitySearchQuery(e.target.value))}
              className="px-3 py-2 w-full rounded-lg shadow bg-primaryBg dark:bg-primaryBgDark dark:text-primaryTextDark text-primaryText"
            />

            {isCityDropdownOpen && (
              <div className="absolute mt-1 w-full rounded-lg bg-primaryBg dark:bg-primaryBgDark dark:text-primaryTextDark text-primaryText shadow-lg max-h-40 overflow-y-auto p-2 z-50 custom-scroll">
                <button
                  onClick={() => {
                    dispatch(setCitySearchQuery("All Cities"));
                    dispatch(
                      setSelectedCity({
                        _id: "all",
                        city: "All cities",
                        country: "",
                      })
                    );
                    setIsCityDropdownOpen(false);
                  }}
                  className="w-full text-left px-2 py-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                >
                  {t("All Cities")}
                </button>

                {availableCities.map((city: City) => (
                  <button
                    key={city._id}
                    onClick={() => {
                      dispatch(setCitySearchQuery(city.city));
                      dispatch(setSelectedCity(city));
                      setIsCityDropdownOpen(false);
                    }}
                    className="w-full text-left px-2 py-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded flex justify-between"
                  >
                    {city.city}, {city.country}
                    {selectedCity?.city === city.city && (
                      <span className="text-green-600 text-xs">✓</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <nav className="flex flex-col gap-4">
            {menu.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`p-2 rounded hover:font-bold dark:hover:font-bold ${
                  isActive(item.path)
                    ? "bg-gray-200 dark:bg-gray-700 font-semibold"
                    : ""
                }`}
              >
                {t(item.name)}
              </Link>
            ))}
          </nav>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Link
            to="/admin/settings"
            className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition"
            title="Settings"
          >
            <Settings className="w-5 h-5" />
          </Link>

          <button
            onClick={handleLogout}
            className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900 transition"
            title="Logout"
          >
            <LogOut className="w-5 h-5 text-red-600" />
          </button>
        </div>
      </aside>

      <main className="flex-1 p-8 overflow-y-auto">{children}</main>
    </div>
  );
}
